import { useState, useCallback } from 'react';
import type { UseAdminAppReturn } from './useAdminApp';
import { messageStructureSchemas } from '@pineseed/spec';
import type { z } from 'zod';

// Extract types from schemas
type Reader = z.infer<typeof messageStructureSchemas.Reader>;
type ReaderWindow = z.infer<typeof messageStructureSchemas.ReaderWindow>;

interface ScrollToEndRequest {
  participantId: string;
  threadId: string;
  conversationId: string;
  workspaceId: string;
  windowSize?: number;
  cacheSize?: number;
}

interface ScrollRequest {
  participantId: string;
  threadId: string;
  amount: number;
}

export interface ReaderAdminHandler {
  // State
  readerState: Reader | null;
  readerWindow: ReaderWindow | null;
  loading: boolean;
  error: string | null;
  
  // Methods
  getReaderState: (participantId: string, threadId: string) => Promise<void>;
  scrollToEnd: (request: ScrollToEndRequest) => Promise<void>;
  scrollUp: (request: ScrollRequest) => Promise<void>;
  scrollDown: (request: ScrollRequest) => Promise<void>;
  getWindow: (participantId: string, threadId: string, limit?: number) => Promise<void>;
  
  // Clear state
  clearState: () => void;
}

export function useReaderAdmin(adminApp: UseAdminAppReturn): ReaderAdminHandler {
  const [readerState, setReaderState] = useState<Reader | null>(null);
  const [readerWindow, setReaderWindow] = useState<ReaderWindow | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getReaderState = useCallback(async (participantId: string, threadId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${adminApp.httpUrl}/api/reader?participantId=${encodeURIComponent(participantId)}&threadId=${encodeURIComponent(threadId)}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'X-Workspace-Id': adminApp.workspaceId },
          credentials: 'include',
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to get reader state: ${response.statusText}`);
      }

      const data = await response.json() as Reader;
      console.log('✅ [ReaderAdmin] Loaded reader state:', data);
      setReaderState(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get reader state';
      setError(errorMessage);
      console.error('❌ [ReaderAdmin] Get reader state error:', errorMessage);
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl]);

  const scrollToEnd = useCallback(async (request: ScrollToEndRequest) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${adminApp.httpUrl}/api/reader/scrollToEnd`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Workspace-Id': adminApp.workspaceId },
        credentials: 'include',
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`Failed to scroll to end: ${response.statusText}`);
      }

      const data = await response.json() as Reader;
      console.log('✅ [ReaderAdmin] Scrolled to end:', data);
      setReaderState(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to scroll to end';
      setError(errorMessage);
      console.error('❌ [ReaderAdmin] Scroll to end error:', errorMessage);
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl]);

  const scrollUp = useCallback(async (request: ScrollRequest) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${adminApp.httpUrl}/api/reader/scrollUp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Workspace-Id': adminApp.workspaceId },
        credentials: 'include',
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`Failed to scroll up: ${response.statusText}`);
      }

      const data = await response.json() as Reader;
      console.log('✅ [ReaderAdmin] Scrolled up:', data);
      setReaderState(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to scroll up';
      setError(errorMessage);
      console.error('❌ [ReaderAdmin] Scroll up error:', errorMessage);
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl]);

  const scrollDown = useCallback(async (request: ScrollRequest) => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${adminApp.httpUrl}/api/reader/scrollDown`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Workspace-Id': adminApp.workspaceId },
        credentials: 'include',
        body: JSON.stringify(request),
      });

      if (!response.ok) {
        throw new Error(`Failed to scroll down: ${response.statusText}`);
      }

      const data = await response.json() as Reader;
      console.log('✅ [ReaderAdmin] Scrolled down:', data);
      setReaderState(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to scroll down';
      setError(errorMessage);
      console.error('❌ [ReaderAdmin] Scroll down error:', errorMessage);
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl]);

  const getWindow = useCallback(async (participantId: string, threadId: string, limit?: number) => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        participantId,
        threadId,
      });
      if (limit !== undefined) {
        params.append('limit', limit.toString());
      }

      const response = await fetch(`${adminApp.httpUrl}/api/reader/window?${params.toString()}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'X-Workspace-Id': adminApp.workspaceId },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to get window: ${response.statusText}`);
      }

      const data = await response.json() as ReaderWindow;
      console.log('✅ [ReaderAdmin] Loaded window:', data);
      setReaderWindow(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get window';
      setError(errorMessage);
      console.error('❌ [ReaderAdmin] Get window error:', errorMessage);
    } finally {
      setLoading(false);
    }
  }, [adminApp.httpUrl]);

  const clearState = useCallback(() => {
    setReaderState(null);
    setReaderWindow(null);
    setError(null);
  }, []);

  return {
    readerState,
    readerWindow,
    loading,
    error,
    getReaderState,
    scrollToEnd,
    scrollUp,
    scrollDown,
    getWindow,
    clearState,
  };
}

